import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { ToastTypes, useMessage } from "../../../../base/context/MessageProvider";
import { useLoader } from "../../../../base/context/LoaderProvider";
import { categoryApiService } from "../../../../service/admin/categoryApiService";
import { useDispatch, useSelector } from "react-redux";
import { globalCategoriesData, addGlobalCategories } from "../../../../redux/slices/categorySlice";
import "./style.css";
import { cronLogService } from "../../../../service/admin/cronLogService";

const getGlobalCategoryNodeId = (category) => category.categoryId || category.c_id || category.id || category._id;
const getGlobalCategoryNodeName = (category) => category.translatedName || category.c_name || category.name;
const getMappedGlobalCategoryLeafId = (mapping) => {
  if (!mapping || typeof mapping !== "object") return null;

  if (Array.isArray(mapping.globalCategoryId) && mapping.globalCategoryId.length > 0) {
    return mapping.globalCategoryId[mapping.globalCategoryId.length - 1];
  }

  return mapping.thirdCategoryId || mapping.secondCategoryId || mapping.topCategoryId || null;
};
const buildGlobalCategoryPath = (mapping) => {
  if (!mapping || typeof mapping !== "object") return "";

  return [
    mapping.topCategoryName,
    mapping.secondCategoryName,
    mapping.thirdCategoryName
  ].filter(Boolean).join(" > ");
};

const GlobalCategoryMappingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [selectedGlobalMappings, setSelectedGlobalMappings] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [bcd, setBcd] = useState("");
  const [gstPercentage, setGstPercentage] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [length, setLength] = useState("");
  const [breadth, setBreadth] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [globalCategories, setGlobalCategories] = useState([]);
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryDetailsLoaded, setCategoryDetailsLoaded] = useState(false);
  const [globalCategoriesLoaded, setGlobalCategoriesLoaded] = useState(false);
  const [currentPricingAlgorithm, setCurrentPricingAlgorithm] = useState({});
  // New fields
  const [minWeight, setMinWeight] = useState(0);
  const [maxWeight, setMaxWeight] = useState(0);
  const [interationalFreightPerKg, setInterationalFreightPerKg] = useState(0);
  const [platformFee, setPlatformFee] = useState(0);
  const [localShipping, setLocalShipping] = useState(0);
  const [markUp, setMarkUp] = useState(0);
  const [priorityDimension, setPriorityDimension] = useState(true);
  const [priorityWeight, setPriorityWeight] = useState(true);
  const [showAlgoModal, setShowAlgoModal] = useState(false);
  const [parentCategoryDetails, setParentCategoryDetails] = useState([]);
  // Track which global category IDs were originally in the DB so we can diff on submit
  const [initialGlobalCategoryIds, setInitialGlobalCategoryIds] = useState([]);
  const initialLoadDone = useRef(false);
  // Show more/less for selected category paths
  const [showAllSelectedPaths, setShowAllSelectedPaths] = useState(false);

  const message = useMessage();
  const loader = useLoader();
  const globalCategoriesFromStore = useSelector(globalCategoriesData);
  const selectedGlobalCategoryIds = selectedGlobalMappings.map((mapping) => mapping.categoryId);
  const selectedGlobalCategoryPaths = selectedGlobalMappings.map((mapping) => mapping.path);

  const getCurrentPricingAlgorithm = async () => {
    try {
      const response = await cronLogService.getPricingAlgorithms();
      if (response.status.code === 200) {
        const commonAlgorithm = Array.isArray(response.data)
          ? response.data.find(item => item.active === true && item.categoryId === null && item.scope === "common" && item.domain == "importerr.com")
          : null;

        setCurrentPricingAlgorithm(commonAlgorithm);
      } else {
        console.error('Failed to fetch pricing algorithm:', response.error);
      }
    } catch (err) {
      console.error('Error fetching pricing algorithm:', err);
    }
  }

  const fetchCategoryDetails = async (categoryId) => {
    try {
      // Fetch category details from the new API endpoint
      const response = await categoryApiService.getCategoryDetails(categoryId);

      if (response.status.code === 200) {
        setCategoryData(prev => ({
          ...prev,
          globalCategories: response.data.globalCategories || [],
          globalCategoryPath: response.data.globalCategoryPath,
          bcd: response.data.bcd, // Backend now returns bcd field properly
          gst_percentage: response.data.gst_percentage,
          hsn_code: response.data.hsn_code,
          length: response.data.length || '',
          breadth: response.data.breadth || '',
          height: response.data.height || '',
          weight: response.data.weight || ''
        }));

        // Set form fields from fetched data
        setLength(response.data.length || '');
        setBreadth(response.data.breadth || '');
        setHeight(response.data.height || '');
        setWeight(response.data.weight || '');
        // Set new fields if present
        setMinWeight(response.data.minWeight || 0);
        setMaxWeight(response.data.maxWeight || 0);
        setInterationalFreightPerKg(response.data.interationalFreightPerKg || 0);
        setPlatformFee(response.data.platformFee || 0);
        setLocalShipping(response.data.localShipping || 0);
        setMarkUp(response.data.markUp || 0);
        if (response.data.priority) {
          setPriorityDimension(response.data.priority === 'dimension' || response.data.priority === 'both');
          setPriorityWeight(response.data.priority === 'weight' || response.data.priority === 'both');
        }
      } else {
        console.error('API returned error:', response.error);
      }
    } catch (error) {
      console.error('Failed to fetch category details:', error);
      // Continue with basic categoryId and categoryName if API fails
    } finally {
      // Mark category details as loaded
      setCategoryDetailsLoaded(true);
    }
  };

  const fetchParentCategoryDetails = async (targetCategoryId) => {
    try {
      const treeRes = await categoryApiService.getExistingCategory();
      if (treeRes.status.code !== 200 || !Array.isArray(treeRes.data)) return;

      const findAncestors = (nodes, targetId, path = []) => {
        for (const node of nodes) {
          const newPath = [...path, { c_id: node.c_id, c_name: node.c_name }];
          if (String(node.c_id) === String(targetId)) {
            return path;
          }
          if (node.children && node.children.length > 0) {
            const result = findAncestors(node.children, targetId, newPath);
            if (result) return result;
          }
        }
        return null;
      };

      const ancestors = findAncestors(treeRes.data, targetCategoryId);
      if (!ancestors || ancestors.length === 0) return;

      const details = [];
      for (const ancestor of ancestors) {
        try {
          const res = await categoryApiService.getCategoryDetails(ancestor.c_id);
          if (res.status.code === 200) {
            details.push({
              categoryId: res.data.categoryId,
              categoryName: res.data.categoryName || ancestor.c_name,
              interationalFreightPerKg: res.data.interationalFreightPerKg || 0,
              platformFee: res.data.platformFee || 0,
              localShipping: res.data.localShipping || 0,
              markUp: res.data.markUp || 0,
              priority: res.data.priority || 'both'
            });
          }
        } catch (err) {
          console.error(`Failed to fetch parent category ${ancestor.c_id}:`, err);
        }
      }
      setParentCategoryDetails(details);
    } catch (err) {
      console.error('Error fetching parent categories:', err);
    }
  };

  // Parse category data from URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryId = searchParams.get('categoryId');
    const categoryName = searchParams.get('categoryName');

    if (categoryId && categoryName) {
      setCategoryData({
        categoryId,
        categoryName
      });
      // Fetch category details from API using categoryId
      fetchCategoryDetails(categoryId);
      fetchParentCategoryDetails(categoryId);
    } else {
      // No category data provided, go back
      navigate('/admin/categories');
      return;
    }
  }, [location, navigate]);

  // Load global categories
  useEffect(() => {
    if (Array.isArray(globalCategoriesFromStore) && globalCategoriesFromStore.length) {
      setGlobalCategories(globalCategoriesFromStore);
      setGlobalCategoriesLoaded(true);
    } else {
      fetchGlobalCategories();
    }
    getCurrentPricingAlgorithm();
  }, []);

  // Auto-select and populate fields when category data is available
  useEffect(() => {
    if (categoryData && globalCategories.length > 0) {
      let resolvedSelectedIds = [];

      if (Array.isArray(categoryData.globalCategories) && categoryData.globalCategories.length > 0) {
        const selectedMappings = [];
        const expandedSet = new Set();

        categoryData.globalCategories.forEach((mapping) => {
          const pathToSelect = buildGlobalCategoryPath(mapping);
          const leafCategoryId = getMappedGlobalCategoryLeafId(mapping);

          if (!pathToSelect || leafCategoryId == null) {
            return;
          }

          const categoryParts = pathToSelect.split(' > ').map(part => part.trim());

          const findAndExpandCategoryByPath = (categories, pathParts) => {
            if (!Array.isArray(categories) || pathParts.length === 0) {
              return null;
            }

            for (const category of categories) {
              const categoryName = getGlobalCategoryNodeName(category);
              const categoryId = getGlobalCategoryNodeId(category);

              if (categoryName === pathParts[0]) {
                if (pathParts.length === 1) {
                  return expandedSet;
                }
                expandedSet.add(categoryId);
                const result = findAndExpandCategoryByPath(category.children, pathParts.slice(1));
                if (result) return result;
              }
            }
            return null;
          };

          findAndExpandCategoryByPath(globalCategories, categoryParts);
          selectedMappings.push({
            categoryId: leafCategoryId,
            path: pathToSelect
          });
        });

        resolvedSelectedIds = selectedMappings.map((mapping) => mapping.categoryId);
        setSelectedGlobalMappings(selectedMappings);
        setExpandedCategories(expandedSet);
      } else if (categoryData.globalCategoryId) {
        const ids = Array.isArray(categoryData.globalCategoryId)
          ? categoryData.globalCategoryId
          : [categoryData.globalCategoryId];
        const paths = Array.isArray(categoryData.globalCategoryPath)
          ? categoryData.globalCategoryPath
          : categoryData.globalCategoryPath ? [categoryData.globalCategoryPath] : [];

        resolvedSelectedIds = ids;
        setSelectedGlobalMappings(ids.map((id, index) => ({
          categoryId: id,
          path: paths[index] || ""
        })));
      }

      // Set initial IDs from the same resolved selection so the diff on submit is accurate
      if (!initialLoadDone.current && categoryDetailsLoaded) {
        setInitialGlobalCategoryIds([...resolvedSelectedIds]);
        initialLoadDone.current = true;
      }

      // Pre-fill form fields
      setBcd(categoryData.bcd || "");
      setGstPercentage(categoryData.gst_percentage || "");
      setHsnCode(categoryData.hsn_code || "");
    }
  }, [categoryData, globalCategories, categoryDetailsLoaded]);

  const fetchGlobalCategories = async () => {
    try {
      const res = await categoryApiService.fetchGlobalCategory();
      if (res.status.code === 200 && res?.data?.length) {
        setGlobalCategories(res.data);
        dispatch(addGlobalCategories(res.data));
      } else if (res.status.code === 402) {
        navigate("/login");
      } else {
        message.showToast(res.error.message, ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Server error fetching global categories", ToastTypes.ERROR);
    } finally {
      // Mark global categories as loaded
      setGlobalCategoriesLoaded(true);
    }
  };

  // Monitor loading states and set main loading to false when both are complete
  useEffect(() => {
    if (categoryDetailsLoaded && globalCategoriesLoaded) {
      setLoading(false);
    }
  }, [categoryDetailsLoaded, globalCategoriesLoaded]);

  // Auto-expand categories when searching
  useEffect(() => {
    if (searchQuery && searchQuery.trim() !== "") {
      const findMatchingCategories = (categories, query, path = []) => {
        const matchingPaths = [];

        const searchRecursive = (cats, currentPath) => {
          cats.forEach((category) => {
            const categoryName = (getGlobalCategoryNodeName(category) || "").toLowerCase();
            const categoryId = getGlobalCategoryNodeId(category);
            const newPath = [...currentPath, categoryId];

            if (categoryName.includes(query.toLowerCase())) {
              matchingPaths.push(...newPath);
            }

            if (category.children && Array.isArray(category.children)) {
              searchRecursive(category.children, newPath);
            }
          });
        };

        searchRecursive(categories, path);
        return matchingPaths;
      };

      const matchingPaths = findMatchingCategories(globalCategories, searchQuery);
      if (matchingPaths.length > 0) {
        setExpandedCategories(new Set(matchingPaths));
      }
    }
  }, [searchQuery, globalCategories]);

  const selectGlobalCategory = (category, path = "") => {
    const categoryId = getGlobalCategoryNodeId(category);
    const categoryName = getGlobalCategoryNodeName(category);
    const fullPath = path ? `${path} > ${categoryName}` : categoryName;

    setSelectedGlobalMappings(prev => {
      const isSelected = prev.some((mapping) => String(mapping.categoryId) === String(categoryId));
      if (isSelected) {
        return prev.filter((mapping) => String(mapping.categoryId) !== String(categoryId));
      }

      return [...prev, { categoryId, path: fullPath }];
    });
  };

  const filterCategories = (categories, query) => {
    if (!query || query.trim() === "") return categories;

    const searchTerm = query.toLowerCase().trim();

    return categories.filter(category => {
      const categoryName = (getGlobalCategoryNodeName(category) || "").toLowerCase();
      const matchesCurrent = categoryName.includes(searchTerm);

      if (matchesCurrent) return true;

      if (category.children && Array.isArray(category.children)) {
        const matchingChildren = filterCategories(category.children, query);
        return matchingChildren.length > 0;
      }

      return false;
    }).map(category => {
      const categoryName = (getGlobalCategoryNodeName(category) || "").toLowerCase();
      if (categoryName.includes(searchTerm)) {
        return category;
      }

      if (category.children && Array.isArray(category.children)) {
        const matchingChildren = filterCategories(category.children, query);
        if (matchingChildren.length > 0) {
          return {
            ...category,
            children: matchingChildren
          };
        }
      }

      return null;
    }).filter(Boolean);
  };

  const renderCategoryTree = (categories, level = 0, parentPath = "") => {
    if (!Array.isArray(categories)) return null;

    return categories.map((category) => {
      const categoryId = getGlobalCategoryNodeId(category);
      const categoryName = getGlobalCategoryNodeName(category);
      const hasChildren = category.children && Array.isArray(category.children) && category.children.length > 0;
      const isSelected = selectedGlobalCategoryIds.some((id) => String(id) === String(categoryId));
      const isExpanded = expandedCategories.has(categoryId);
      const currentPath = parentPath ? `${parentPath} > ${categoryName}` : categoryName;

      return (
        <div key={categoryId} className="select-none">
          <div
            className={`flex items-center py-2 px-3 rounded-lg cursor-pointer transition-colors ${!hasChildren && isSelected
              ? "bg-purple-100 text-purple-900 border border-purple-300"
              : "hover:bg-slate-100 text-slate-700"
              }`}
            style={{ paddingLeft: `${level * 20 + 12}px` }}
            onClick={() => {
              if (hasChildren) {
                setExpandedCategories(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(categoryId)) {
                    newSet.delete(categoryId);
                  } else {
                    newSet.add(categoryId);
                  }
                  return newSet;
                });
              } else {
                selectGlobalCategory(category, parentPath);
              }
            }}
          >
            {hasChildren ? (
              <div className="w-5 h-5 mr-2 flex items-center justify-center text-slate-400">
                {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
              </div>
            ) : (
              <div className="w-5 h-5 mr-2 flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => { }}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectGlobalCategory(category, parentPath);
                  }}
                />
              </div>
            )}
            <span className="text-sm font-medium">{categoryName}</span>
          </div>
          {hasChildren && isExpanded && (
            <div className="mt-1">
              {renderCategoryTree(category.children, level + 1, currentPath)}
            </div>
          )}
        </div>
      );
    });
  };

  const handleSubmit = async () => {
    if (selectedGlobalCategoryIds.length === 0) {
      message.showToast("Please select at least one global category", ToastTypes.ERROR);
      return;
    }

    if (!bcd) {
      message.showToast("BCD (Basic Customs Duty) is required", ToastTypes.ERROR);
      return;
    }
    if (!gstPercentage) {
      message.showToast("GST Percentage is required", ToastTypes.ERROR);
      return;
    }
    if (!hsnCode) {
      message.showToast("HSN Code is required", ToastTypes.ERROR);
      return;
    }
    if (!length || Number(length) <= 0) {
      message.showToast("Length is required and must be a positive number", ToastTypes.ERROR);
      return;
    }
    if (!breadth || Number(breadth) <= 0) {
      message.showToast("Breadth is required and must be a positive number", ToastTypes.ERROR);
      return;
    }
    if (!height || Number(height) <= 0) {
      message.showToast("Height is required and must be a positive number", ToastTypes.ERROR);
      return;
    }
    if (!weight || Number(weight) <= 0) {
      message.showToast("Weight is required and must be a positive number", ToastTypes.ERROR);
      return;
    }
    if (!priorityDimension && !priorityWeight) {
      message.showToast("At least one priority (Dimension or Weight) must be selected", ToastTypes.ERROR);
      return;
    }

    try {
      setIsSubmitting(true);
      loader.showLoading(true);

      // Compute additions and removals in one diff
      const idsToRemove = initialGlobalCategoryIds.filter(
        id => !selectedGlobalCategoryIds.some(selId => String(selId) === String(id))
      );

      const reqBody = {
        categoryId: categoryData.categoryId,
        globalCategoryIds: selectedGlobalCategoryIds,
        removeGlobalCategoryIds: idsToRemove,
        bcd: bcd,
        gst_percentage: gstPercentage,
        hsn_code: hsnCode,
        length: length,
        breadth: breadth,
        height: height,
        weight: weight,
        minWeight,
        maxWeight,
        interationalFreightPerKg,
        platformFee,
        localShipping,
        markUp,
        priority: priorityDimension && priorityWeight ? 'both' : priorityDimension ? 'dimension' : 'weight'
      };

      const result = await categoryApiService.mapImporterrCategoryWithGlobalCategory(JSON.stringify(reqBody));
      const allSuccessful = result?.status?.code === 200;

      if (allSuccessful) {
        loader.showLoading(false);
        // Update baseline so a re-submit without navigation also diffs correctly
        setInitialGlobalCategoryIds([...selectedGlobalCategoryIds]);
        message.showToast(
          `Category "${categoryData.categoryName}" mapped to ${selectedGlobalCategoryIds.length} global categories successfully`,
          ToastTypes.SUCCESS
        );

        // Navigate back to categories page after successful mapping
        setTimeout(() => {
          navigate('/admin/categories');
        }, 1500);
      } else {
        throw new Error('Some mappings failed');
      }
    } catch (error) {
      loader.showLoading(false);
      message.showToast(
        error?.message || "Failed to map global categories",
        ToastTypes.ERROR
      );
    } finally {
      setIsSubmitting(false);
      loader.showLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/categories');
  };

  if (loading) {
    return (
      <div className="global-mapping-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!categoryData) {
    return (
      <div className="global-mapping-page">
        <div className="error-container">
          <p>No category data provided</p>
          {/* <button onClick={handleBack} className="back-btn">
            <FiArrowLeft /> Back to Categories
          </button> */}
        </div>
      </div>
    );
  }

  return (
    <div className="cat-page">
      {/* Header */}
      <div className="cat-header">
        <div className="cat-header-left">
          <button className="cat-back-btn" onClick={handleBack}>
            <IoChevronBack /> Back
          </button>
          <div className="cat-breadcrumb">
            <span className="cat-breadcrumb-item">Categories</span>
            <IoChevronForward className="cat-breadcrumb-sep" />
            <span className="cat-breadcrumb-item">{categoryData.categoryName}</span>
            <IoChevronForward className="cat-breadcrumb-sep" />
            <span className="cat-breadcrumb-item">Map Global Category</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mapping-content">
        <div className="mapping-grid">
          {/* Left Column: Category Selection */}
          <div className="mapping-column column-left">
            {/* Category Info */}
            <div className="category-info-card compact">
              <label className="info-label">Importerr Category</label>
              <div className="category-name">{categoryData.categoryName}</div>
            </div>

            {/* Search Bar */}
            <div className="search-section">
              <div className="search-container">
                <label className="section-label">Search</label>
                <input
                  type="text"
                  placeholder="Search global categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="input-search-clear"
                  >
                    X
                  </button>
                )}
              </div>
            </div>

            {/* Global Category Tree */}
            <div className="category-tree-section">
              <label className="section-label">Select Global Category</label>
              <div className="category-tree-container">
                {globalCategories?.length > 0 ? (
                  <div className="category-tree">
                    {renderCategoryTree(filterCategories(globalCategories, searchQuery))}
                  </div>
                ) : (
                  <div className="no-categories">
                    No global categories available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Config & Selection details */}
          <div className="mapping-column column-right">
            {/* Selected Categories List */}
            <div className="selected-category-card compact">
              <label className="info-label">Selected Categories ({selectedGlobalCategoryPaths.length})</label>
              <div className="selected-category-paths">
                {selectedGlobalCategoryPaths.length > 0 ? (
                  <>
                    {/* Show only 2 by default, with Show More/Show Less toggle */}
                    {(showAllSelectedPaths ? selectedGlobalCategoryPaths : selectedGlobalCategoryPaths.slice(0, 2)).map((path, index) => (
                      <div key={index} className="selected-path-item">
                        {path}
                      </div>
                    ))}
                    {selectedGlobalCategoryPaths.length > 2 && (
                      <button
                        type="button"
                        className="show-more-btn"
                        style={{ marginTop: 4, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onClick={() => setShowAllSelectedPaths((prev) => !prev)}
                      >
                        {showAllSelectedPaths ? 'Show Less' : `Show More (${selectedGlobalCategoryPaths.length - 2})`}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="no-selection-text" style={{ color: '#9ca3af', fontSize: '13px', padding: '4px 0' }}>
                    No global category selected yet
                  </div>
                )}
              </div>
            </div>

            {/* Form Fields */}
            <div className="form-section compact">
              <div className="weight-fields compact">
                <label className="field-label">Priority</label>
                <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={priorityDimension}
                      onChange={e => setPriorityDimension(e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    Dimension
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={priorityWeight}
                      onChange={e => setPriorityWeight(e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    Weight
                  </label>
                </div>
              </div>
              {/* Dimension Fields */}
              <div className="dimension-fields compact">
                <label className="field-label">Dimensions</label>
                <span style={{ marginLeft: '12px', fontSize: '13px', color: '#6b7280' }}>
                  Volumetric Weight: {
                    length && breadth && height && Number(length) > 0 && Number(breadth) > 0 && Number(height) > 0
                      ? ((Number(length) * Number(breadth) * Number(height)) / 5000).toFixed(4) + ' kg'
                      : '--'
                  }
                </span>
                <div className="gc-form-row gc-form-row-three">
                  <div className="form-field">
                    <label className="field-label">Length (cm) *</label>
                    <input
                      type="number"
                      placeholder="length(in cm)"
                      value={length}
                      onChange={(e) => setLength(e.target.value)}
                      className="field-input"
                      min="0"
                      step="0.1"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">Breadth (cm) *</label>
                    <input
                      type="number"
                      placeholder="breadth(in cm)"
                      value={breadth}
                      onChange={(e) => setBreadth(e.target.value)}
                      className="field-input"
                      min="0"
                      step="0.1"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">Height (cm) *</label>
                    <input
                      type="number"
                      placeholder="height(in cm)"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      className="field-input"
                      min="0"
                      step="0.1"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Weight Fields */}
              <div className="weight-fields compact">
                <label className="field-label">Weight</label>
                <div className="gc-form-row">
                  <div className="form-field">
                    <label className="field-label">Weight (grams) *</label>
                    <input
                      type="number"
                      placeholder="Grams"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      className="field-input"
                      min="0"
                      step="0.1"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">Weight (kg)</label>
                    <input
                      type="number"
                      placeholder="Kilograms"
                      value={weight !== '' ? parseFloat((weight / 1000).toFixed(4)) : ''}
                      onChange={(e) => {
                        const kg = e.target.value;
                        setWeight(kg !== '' ? parseFloat((kg * 1000).toFixed(4)) : '');
                      }}
                      className="field-input"
                      min="0"
                      step="0.001"
                    />
                  </div>
                </div>
              </div>

              <div className="weight-fields compact">
                <label className="field-label">Tax Details</label>
                <div className="gc-form-row">
                  <div className="form-field">
                    <label className="field-label">Basic Custom Duty(BCD in %)</label>
                    <input
                      type="number"
                      placeholder="2%"
                      value={bcd}
                      onChange={(e) => setBcd(e.target.value)}
                      className="field-input"
                      required
                    />
                    <small className="text-gray-500">(Basic Custom Duty + 10% Surcharge)</small>
                  </div>

                  <div className="form-field">
                    <label className="field-label">GST (%)</label>
                    <input
                      type="number"
                      placeholder="2%"
                      value={gstPercentage}
                      onChange={(e) => setGstPercentage(e.target.value)}
                      className="field-input"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="weight-fields compact">
                <label className="field-label">HSN</label>
                <div className="form-field">
                  <label className="field-label">HSN Code</label>
                  <input
                    type="text"
                    placeholder="Enter HSN"
                    value={hsnCode}
                    onChange={(e) => setHsnCode(e.target.value)}
                    className="field-input"
                    required
                  />
                </div>
              </div>

              <div className="weight-fields compact">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className="pb-2">
                  <label className="field-label" style={{ margin: 0 }}>Advanced Shipping/Tax Fields</label>
                  <button
                    type="button"
                    onClick={() => setShowAlgoModal(true)}
                    style={{ padding: '4px 12px', fontSize: 12, background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                  >
                    Show Algorithm
                  </button>
                </div>
                <div className="gc-form-row gc-form-row-three">
                  <div className="form-field">
                    <label className="field-label">
                      Min Weight (grams)
                      <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 13 }}>
                        {minWeight ? `(${(minWeight / 1000).toFixed(4)} kg)` : ''}
                      </span>
                    </label>
                    <input
                      type="number"
                      placeholder="Min Weight (Grams)"
                      value={minWeight}
                      onChange={e => setMinWeight(Number(e.target.value))}
                      className="field-input"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">
                      Max Weight (grams)
                      <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 13 }}>
                        {maxWeight ? `(${(maxWeight / 1000).toFixed(4)} kg)` : ''}
                      </span>
                    </label>
                    <input
                      type="number"
                      placeholder="Max Weight (Grams)"
                      value={maxWeight}
                      onChange={e => setMaxWeight(Number(e.target.value))}
                      className="field-input"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">International Freight/Kg (INR)</label>
                    <input
                      type="number"
                      placeholder="International Freight Per Kg"
                      value={interationalFreightPerKg}
                      onChange={e => setInterationalFreightPerKg(Number(e.target.value))}
                      className="field-input"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <div className="gc-form-row gc-form-row-three">
                  <div className="form-field">
                    <label className="field-label">
                      Platform Fee(%)
                      <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 13 }}>
                        {platformFee ? `(${(platformFee / 100).toFixed(2)})` : ''}
                      </span>
                    </label>
                    <input
                      type="number"
                      placeholder="Platform Fee"
                      value={platformFee}
                      onChange={e => setPlatformFee(Number(e.target.value))}
                      className="field-input"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">Local Shipping(RMB)</label>
                    <input
                      type="number"
                      placeholder="Local Shipping"
                      value={localShipping}
                      onChange={e => setLocalShipping(Number(e.target.value))}
                      className="field-input"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">
                      Markup (%)
                      <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 13 }}>
                        {markUp ? `(${(markUp / 100).toFixed(2)})` : ''}
                      </span>
                    </label>
                    <input
                      type="number"
                      placeholder="Markup"
                      value={markUp}
                      onChange={e => setMarkUp(Number(e.target.value))}
                      className="field-input"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Algo Modal */}
      {showAlgoModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowAlgoModal(false)}
        >
          <div
            style={{ background: '#fff', borderRadius: 12, padding: 24, minWidth: 400, maxWidth: 560, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.15)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Main Pricing Algorithm</h3>
              {currentPricingAlgorithm?.formula && (
                <button
                  type="button"
                  onClick={() => {
                    const f = currentPricingAlgorithm.formula;
                    if (f) {
                      if (f.international_freight_per_kg != null) setInterationalFreightPerKg(f.international_freight_per_kg);
                      if (f.platform_fee_percent != null) setPlatformFee(f.platform_fee_percent * 100);
                      if (f.local_shipping_rmb != null) setLocalShipping(f.local_shipping_rmb);
                      if (f.markup_percent != null) setMarkUp(f.markup_percent * 100);
                    }
                    setShowAlgoModal(false);
                  }}
                  style={{ padding: '3px 10px', fontSize: 11, background: '#6366f1', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                >
                  Copy
                </button>
              )}
            </div>
            {currentPricingAlgorithm?.formula ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                  <span style={{ color: '#6b7280' }}>International Freight/Kg</span>
                  <span style={{ fontWeight: 500 }}>{currentPricingAlgorithm.formula.international_freight_per_kg ?? '--'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                  <span style={{ color: '#6b7280' }}>Platform Fee (%)</span>
                  <span style={{ fontWeight: 500 }}>{currentPricingAlgorithm.formula.platform_fee_percent != null ? currentPricingAlgorithm.formula.platform_fee_percent * 100 + '%' : '--'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                  <span style={{ color: '#6b7280' }}>Local Shipping (RMB)</span>
                  <span style={{ fontWeight: 500 }}>{currentPricingAlgorithm.formula.local_shipping_rmb ?? '--'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                  <span style={{ color: '#6b7280' }}>Markup (%)</span>
                  <span style={{ fontWeight: 500 }}>{currentPricingAlgorithm.formula.markup_percent != null ? currentPricingAlgorithm.formula.markup_percent * 100 + "%" : '--'}</span>
                </div>
              </div>
            ) : (
              <p style={{ color: '#9ca3af', fontSize: 14 }}>No pricing algorithm data available.</p>
            )}

            {/* Parent Categories */}
            {parentCategoryDetails.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#374151' }}>Parent Categories</h4>
                {parentCategoryDetails.map((parent, idx) => (
                  <div key={parent.categoryId || idx} style={{ marginBottom: 14, padding: 12, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{parent.categoryName}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setInterationalFreightPerKg(parent.interationalFreightPerKg);
                          setPlatformFee(parent.platformFee);
                          setLocalShipping(parent.localShipping);
                          setMarkUp(parent.markUp);
                          setShowAlgoModal(false);
                        }}
                        style={{ padding: '3px 10px', fontSize: 11, background: '#6366f1', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                      >
                        Copy
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#6b7280' }}>International Freight/Kg</span>
                        <span style={{ fontWeight: 500 }}>{parent.interationalFreightPerKg || '--'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#6b7280' }}>Platform Fee (%)</span>
                        <span style={{ fontWeight: 500 }}>{parent.platformFee || '--'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#6b7280' }}>Local Shipping (RMB)</span>
                        <span style={{ fontWeight: 500 }}>{parent.localShipping || '--'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#6b7280' }}>Markup (%)</span>
                        <span style={{ fontWeight: 500 }}>{parent.markUp || '--'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowAlgoModal(false)}
                style={{ padding: '6px 18px', fontSize: 13, background: '#e5e7eb', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="mapping-footer">
        <button
          type="button"
          className="cancel-btn"
          onClick={handleBack}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="submit-btn"
          onClick={handleSubmit}
          disabled={isSubmitting || selectedGlobalCategoryIds.length === 0}
        >
          {isSubmitting ? "Mapping..." : "Map Category"}
        </button>
      </div>
    </div>
  );
};

export default GlobalCategoryMappingPage;
